import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Stethoscope,
  Building2,
  Shield,
  Eye,
  Pencil,
  Ban,
  Trash2,
  Key,
  CheckCircle,
  XCircle,
  Loader2,
  Clock,
  AlertTriangle,
  History,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

const UserDetailModal = ({ user, isOpen, onClose, onUserUpdated }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [userDetail, setUserDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (isOpen && user?.id) {
      loadUserDetail();
      setActiveTab('profile');
      setEditing(false);
    }
  }, [isOpen, user?.id]);

  const loadUserDetail = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getUserDetail(user.id);
      setUserDetail(response.data);
      setEditForm({
        full_name: response.data.user.full_name || '',
        email: response.data.user.email || '',
        phone: response.data.user.phone || '',
        location: response.data.user.location || '',
        gender: response.data.user.gender || '',
        specialty: response.data.user.specialty || '',
        professional_type: response.data.user.professional_type || '',
        license_number: response.data.user.license_number || '',
      });
    } catch (error) {
      console.error('Error loading user detail:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await adminAPI.updateUser(user.id, editForm);
      toast.success('Utilisateur mis à jour');
      setEditing(false);
      loadUserDetail();
      if (onUserUpdated) onUserUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async () => {
    setActionLoading('suspend');
    try {
      const newStatus = !userDetail.user.is_suspended;
      await adminAPI.suspendUser(user.id, newStatus);
      toast.success(newStatus ? 'Utilisateur suspendu' : 'Utilisateur réactivé');
      loadUserDetail();
      if (onUserUpdated) onUserUpdated();
    } catch (error) {
      toast.error('Erreur lors de l\'opération');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async () => {
    setActionLoading('reset');
    try {
      await adminAPI.resetUserPassword(user.id);
      toast.success('Email de réinitialisation envoyé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    setActionLoading('delete');
    try {
      await adminAPI.deleteUser(user.id);
      toast.success('Utilisateur supprimé');
      setShowDeleteConfirm(false);
      onClose();
      if (onUserUpdated) onUserUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="w-5 h-5 text-purple-500" />;
      case 'professional': return <Stethoscope className="w-5 h-5 text-red-500" />;
      case 'facility': return <Building2 className="w-5 h-5 text-indigo-500" />;
      default: return <User className="w-5 h-5 text-blue-500" />;
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-700',
      professional: 'bg-red-100 text-red-600',
      facility: 'bg-indigo-100 text-indigo-700',
      agent: 'bg-amber-100 text-amber-700',
      patient: 'bg-blue-100 text-blue-700',
    };
    const labels = {
      admin: 'Admin',
      professional: 'Professionnel',
      facility: 'Établissement',
      agent: 'Agent',
      patient: 'Patient',
    };
    return (
      <Badge className={colors[role] || colors.patient}>
        {labels[role] || role}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (!user) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-3 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getRoleIcon(user.role)}
                <div>
                  <DialogTitle className="text-lg">{user.full_name}</DialogTitle>
                  <DialogDescription className="flex items-center gap-2 mt-1">
                    {getRoleBadge(user.role)}
                    {user.is_suspended && (
                      <Badge className="bg-red-100 text-red-700">Suspendu</Badge>
                    )}
                    {user.is_verified && (
                      <Badge className="bg-emerald-100 text-emerald-700">Vérifié</Badge>
                    )}
                  </DialogDescription>
                </div>
              </div>
            </div>
          </DialogHeader>

          {loading ? (
            <div className="flex-1 flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-slate-300 animate-spin" />
            </div>
          ) : userDetail ? (
            <div className="flex-1 overflow-hidden flex flex-col">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
                <TabsList className="grid grid-cols-3 mx-6 mt-4">
                  <TabsTrigger value="profile" className="text-xs sm:text-sm">
                    <Eye className="w-4 h-4 mr-1" />
                    Aperçu
                  </TabsTrigger>
                  <TabsTrigger value="edit" className="text-xs sm:text-sm">
                    <Pencil className="w-4 h-4 mr-1" />
                    Modifier
                  </TabsTrigger>
                  <TabsTrigger value="activity" className="text-xs sm:text-sm">
                    <History className="w-4 h-4 mr-1" />
                    Activité
                  </TabsTrigger>
                </TabsList>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {/* Profile Preview Tab */}
                  <TabsContent value="profile" className="mt-0 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Card className="border shadow-sm">
                        <CardContent className="p-4 space-y-3">
                          <h4 className="font-medium text-slate-900 text-sm">Informations personnelles</h4>
                          
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-600">{userDetail.user.email || '-'}</span>
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-600">{userDetail.user.phone || '-'}</span>
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-600">{userDetail.user.location || '-'}</span>
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-600">
                              Inscrit le {formatDate(userDetail.user.created_at)}
                            </span>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Role-specific info */}
                      {userDetail.user.role === 'professional' && userDetail.professional_profile && (
                        <Card className="border shadow-sm">
                          <CardContent className="p-4 space-y-3">
                            <h4 className="font-medium text-slate-900 text-sm">Profil professionnel</h4>
                            
                            <div className="text-sm space-y-2">
                              <div className="text-sm"><span className="text-slate-500">Spécialité:</span> {userDetail.professional_profile.specialty || '-'}</div>
                              <div className="text-sm"><span className="text-slate-500">Type:</span> {userDetail.professional_profile.professional_type || '-'}</div>
                              <div className="text-sm"><span className="text-slate-500">N° Licence:</span> {userDetail.professional_profile.license_number || '-'}</div>
                              <div className="text-sm">
                                <span className="text-slate-500">Statut:</span>{' '}
                                {userDetail.professional_profile.is_validated ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">Validé</Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-700 text-xs">En attente</Badge>
                                )}
                              </div>
                            </div>
                            
                            {userDetail.appointment_stats && (
                              <div className="pt-2 border-t">
                                <p className="text-sm">
                                  <span className="text-slate-500">RDV total:</span>{' '}
                                  <strong>{userDetail.appointment_stats.total}</strong> ({userDetail.appointment_stats.completed} terminés)
                                </p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {userDetail.user.role === 'facility' && userDetail.facility_profile && (
                        <Card className="border shadow-sm">
                          <CardContent className="p-4 space-y-3">
                            <h4 className="font-medium text-slate-900 text-sm">Profil établissement</h4>
                            
                            <div className="text-sm space-y-2">
                              <div className="text-sm"><span className="text-slate-500">Nom:</span> {userDetail.facility_profile.name || '-'}</div>
                              <div className="text-sm"><span className="text-slate-500">Type:</span> {userDetail.facility_profile.facility_type || '-'}</div>
                              <div className="text-sm"><span className="text-slate-500">Adresse:</span> {userDetail.facility_profile.address || '-'}</div>
                              <div className="text-sm">
                                <span className="text-slate-500">KYC:</span>{' '}
                                <Badge className={
                                  userDetail.facility_profile.kyc_status === 'approved' 
                                    ? 'bg-emerald-100 text-emerald-700 text-xs'
                                    : 'bg-amber-100 text-amber-700 text-xs'
                                }>
                                  {userDetail.facility_profile.kyc_status || 'pending'}
                                </Badge>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {userDetail.user.role === 'patient' && (
                        <Card className="border shadow-sm">
                          <CardContent className="p-4 space-y-3">
                            <h4 className="font-medium text-slate-900 text-sm">Activité patient</h4>
                            
                            <div className="text-sm space-y-2">
                              <p><span className="text-slate-500">RDV pris:</span> {userDetail.appointments_count || 0}</p>
                              <p><span className="text-slate-500">Membres famille:</span> {userDetail.family_members_count || 0}</p>
                              <p><span className="text-slate-500">Genre:</span> {userDetail.user.gender || '-'}</p>
                              <p><span className="text-slate-500">Âge:</span> {userDetail.user.age || '-'}</p>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>

                    {/* Quick Actions */}
                    <Card className="border shadow-sm">
                      <CardContent className="p-4">
                        <h4 className="font-medium text-slate-900 text-sm mb-3">Actions rapides</h4>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleResetPassword}
                            disabled={actionLoading === 'reset' || userDetail.user.role === 'admin'}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            {actionLoading === 'reset' ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Key className="w-4 h-4 mr-1" />
                            )}
                            Reset mot de passe
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleSuspend}
                            disabled={actionLoading === 'suspend' || userDetail.user.role === 'admin'}
                            className={userDetail.user.is_suspended 
                              ? 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'
                              : 'text-amber-600 border-amber-200 hover:bg-amber-50'
                            }
                          >
                            {actionLoading === 'suspend' ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : userDetail.user.is_suspended ? (
                              <CheckCircle className="w-4 h-4 mr-1" />
                            ) : (
                              <Ban className="w-4 h-4 mr-1" />
                            )}
                            {userDetail.user.is_suspended ? 'Réactiver' : 'Suspendre'}
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={userDetail.user.role === 'admin'}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Supprimer
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Edit Tab */}
                  <TabsContent value="edit" className="mt-0 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Nom complet</Label>
                        <Input
                          value={editForm.full_name}
                          onChange={(e) => setEditForm(p => ({ ...p, full_name: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-sm">Email</Label>
                        <Input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm(p => ({ ...p, email: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-sm">Téléphone</Label>
                        <Input
                          value={editForm.phone}
                          onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-sm">Localisation</Label>
                        <Input
                          value={editForm.location}
                          onChange={(e) => setEditForm(p => ({ ...p, location: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-sm">Genre</Label>
                        <Select
                          value={editForm.gender}
                          onValueChange={(v) => setEditForm(p => ({ ...p, gender: v }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Homme</SelectItem>
                            <SelectItem value="female">Femme</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {userDetail.user.role === 'professional' && (
                        <>
                          <div>
                            <Label className="text-sm">Spécialité</Label>
                            <Input
                              value={editForm.specialty}
                              onChange={(e) => setEditForm(p => ({ ...p, specialty: e.target.value }))}
                              className="mt-1"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-sm">N° Licence</Label>
                            <Input
                              value={editForm.license_number}
                              onChange={(e) => setEditForm(p => ({ ...p, license_number: e.target.value }))}
                              className="mt-1"
                            />
                          </div>
                        </>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => setActiveTab('profile')}
                      >
                        Annuler
                      </Button>
                      <Button
                        onClick={handleSaveEdit}
                        disabled={saving}
                        className="bg-[#E53935] hover:bg-[#C62828]"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Enregistrer
                      </Button>
                    </div>
                  </TabsContent>

                  {/* Activity Tab */}
                  <TabsContent value="activity" className="mt-0 space-y-4">
                    <h4 className="font-medium text-slate-900 text-sm">Derniers rendez-vous</h4>
                    
                    {userDetail.recent_appointments?.length > 0 ? (
                      <div className="space-y-2">
                        {userDetail.recent_appointments.map((apt, index) => (
                          <Card key={index} className="border shadow-sm">
                            <CardContent className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                                  <Calendar className="w-4 h-4 text-slate-500" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium">{apt.date} à {apt.time_slot}</p>
                                  <p className="text-xs text-slate-500">
                                    {userDetail.user.role === 'patient' 
                                      ? apt.professional_name 
                                      : apt.patient_name}
                                  </p>
                                </div>
                              </div>
                              <Badge className={
                                apt.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                apt.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                                apt.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-amber-100 text-amber-700'
                              }>
                                {apt.status}
                              </Badge>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Aucune activité récente</p>
                      </div>
                    )}
                  </TabsContent>
                </div>
              </Tabs>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Supprimer l'utilisateur
            </DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Toutes les données de l'utilisateur seront définitivement supprimées.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-slate-600">
              Êtes-vous sûr de vouloir supprimer <strong>{user?.full_name}</strong> ?
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={actionLoading === 'delete'}
            >
              {actionLoading === 'delete' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserDetailModal;
